/**
 * Owner: Masa Bayu
 * File: AdminSystemScreen.tsx
 * Date:Time: 2026-10-23 11:35
 * Description: §467-D — seksyen Sistem: kiraan peringkat stor hub (katalog,
 *   fail geran, sesi, direktori artifak/takedown) beserta rujukan ID sebenar.
 *   Semua angka ialah `counts`/`catalog` yang dibalut pelayan — klien tidak
 *   mengira, tidak meneka, dan tidak menyenaraikan fail yang tiada.
 */

import type { ReactElement } from 'react';
import type { AdminLabels } from './admin-labels';
import type { AdminDashboardView } from './admin-dashboard';
import { NO_DATA, dateTimeText } from './admin-view';

interface Stat {
  readonly id: string;
  readonly label: string;
  readonly value: string;
}

function RefList({
  title,
  refs,
  la,
}: {
  title: string;
  refs: readonly string[];
  la: AdminLabels;
}): ReactElement {
  return (
    <section className="qxa__card" aria-label={title}>
      <div className="qxa__card-head">
        <h2 className="qxa__card-title">{title}</h2>
        <span className="qxa__metric">{String(refs.length)}</span>
      </div>
      {refs.length === 0 ? (
        <p className="qxa__empty">{la.refsEmpty}</p>
      ) : (
        <ul className="qxa__id-list">
          {refs.map((ref) => (
            <li key={ref} className="qxa__id-row">
              <code>{ref}</code>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export interface AdminSystemScreenProps {
  readonly view: AdminDashboardView;
  readonly la: AdminLabels;
}

export function AdminSystemScreen({ view, la }: AdminSystemScreenProps): ReactElement {
  const version = view.catalog.version === null ? NO_DATA : String(view.catalog.version);
  const stats: readonly Stat[] = [
    { id: 'catalog-version', label: la.catalogVersion, value: version },
    {
      id: 'catalog-updated',
      label: la.catalogUpdated,
      value: view.catalog.updatedAt === null ? NO_DATA : dateTimeText(view.catalog.updatedAt),
    },
    { id: 'publications', label: la.kpiPublications, value: String(view.catalog.publications) },
    {
      id: 'priced',
      label: la.kpiPriced,
      value: `${String(view.catalog.pricedPublications)}/${String(view.catalog.publications)}`,
    },
    { id: 'entitlements', label: la.entitlementFiles, value: String(view.counts.entitlementFiles) },
    { id: 'grants', label: la.kpiActiveGrants, value: String(view.counts.activeGrants) },
    { id: 'sessions-total', label: la.sessionTotal, value: String(view.counts.sessions.total) },
    { id: 'sessions-active', label: la.sessionActive, value: String(view.counts.sessions.active) },
    { id: 'artifacts', label: la.kpiArtifacts, value: String(view.counts.artifactDirs) },
    { id: 'takedowns', label: la.kpiTakedowns, value: String(view.counts.takedownDirs) },
  ];

  return (
    <div className="qxa__stack">
      <section className="qxa__card" aria-label={la.systemTitle}>
        <div className="qxa__card-head">
          <h2 className="qxa__card-title">{la.systemTitle}</h2>
        </div>
        <p className="qxa__sub">{la.systemBody}</p>
        <div className="qxa__kpis qxa__kpis--dense">
          {stats.map((stat) => (
            <div key={stat.id} className="qxa__kpi">
              <p className="qxa__kpi-label">{stat.label}</p>
              <p className="qxa__kpi-value">{stat.value}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="qxa__cols">
        <RefList title={la.artifactRefs} refs={view.artifactRefs} la={la} />
        <RefList title={la.takedownRefs} refs={view.takedownRefs} la={la} />
      </div>

      <section className="qxa__card" aria-label={la.unownedTitle}>
        <div className="qxa__card-head">
          <h2 className="qxa__card-title">{la.unownedTitle}</h2>
          <span className="qxa__metric">{String(view.catalog.unownedPublications.length)}</span>
        </div>
        {view.catalog.unownedPublications.length === 0 ? (
          <p className="qxa__empty">{la.none}</p>
        ) : (
          <ul className="qxa__id-list">
            {view.catalog.unownedPublications.map((id) => (
              <li key={id} className="qxa__id-row">
                <code>{id}</code>
              </li>
            ))}
          </ul>
        )}
        <p className="qxa__note">{la.unownedBody}</p>
      </section>
    </div>
  );
}
